import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, CheckCircle, ChevronDown, ChevronUp, ExternalLink, Sparkles, AlertTriangle, RefreshCw, XCircle } from 'lucide-react';
import { useQAProcessingContext } from '../contexts/QAProcessingContext';
import { qaService } from '../services/api';

const QAProcessingIndicator = ({ onNavigateToProject }) => {
  const { activeProcesses, pendingStarts, removeProcess } = useQAProcessingContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  // Merge active processes with pending starts for unified display
  const allDisplayItems = [
    ...pendingStarts.map(p => {
      const checkedPages = p.checkedPages || 0;
      const totalPages = p.totalPages || 0;
      const linkProgress = totalPages > 0 ? Math.round((checkedPages / totalPages) * 100) : 0;
      return {
        ...p,
        isPending: true,
        status: p.stage || 'initializing',
        progress: linkProgress,
        checkedPages,
        completedPages: 0,
      };
    }),
    ...activeProcesses,
  ];

  const getElapsedTime = (startedAt) => {
    const elapsed = Math.floor((Date.now() - startedAt) / 1000);
    if (elapsed < 60) return `${elapsed}s`;
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    }
    return `${minutes}m ${seconds}s`;
  };

  const handleRetryFailed = async (projectId) => {
    setActionLoading(`retry-${projectId}`);
    try {
      await qaService.rerunFailedPages(projectId);
      removeProcess(projectId);
    } catch (err) {
      console.error('Failed to retry:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelStuck = async (projectId) => {
    setActionLoading(`cancel-${projectId}`);
    try {
      await qaService.cancelStuckProcessing(projectId);
      removeProcess(projectId);
    } catch (err) {
      console.error('Failed to cancel:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusText = (process) => {
    switch (process.status) {
      case 'completed':
        return 'Completed!';
      case 'stuck':
        return process.failedPages > 0
          ? `Stalled — ${process.failedPages} page(s) failed`
          : 'Processing appears stuck';
      case 'initializing':
        return 'Starting QA...';
      case 'checking_links':
        if (process.checkedPages > 0) {
          return `Checking links... (${process.checkedPages}/${process.totalPages} pages)`;
        }
        return 'Checking links...';
      case 'forwarding_to_n8n':
        return 'Sending to QA workflow...';
      case 'processing':
        return `Analyzing pages... (${process.completedPages}/${process.totalPages})`;
      default:
        return 'Processing...';
    }
  };

  const getCollapsedText = () => {
    const initializingCount = pendingStarts.length;
    const processingCount = activeProcesses.filter(p => p.status === 'processing').length;
    const completedCount = activeProcesses.filter(p => p.status === 'completed').length;
    const stuckCount = activeProcesses.filter(p => p.status === 'stuck').length;
    const runningTotal = initializingCount + processingCount;

    if (stuckCount > 0) {
      return `QA Stalled (${stuckCount})`;
    } else if (runningTotal > 0) {
      return `QA Running (${runningTotal})`;
    } else if (completedCount > 0) {
      return 'QA Complete';
    }
    return 'QA Running';
  };

  const [, forceUpdate] = useState(0);
  useEffect(() => {
    if (allDisplayItems.length === 0) return;
    const interval = setInterval(() => forceUpdate(n => n + 1), 1000);
    return () => clearInterval(interval);
  }, [allDisplayItems.length]);

  if (allDisplayItems.length === 0) return null;

  const processingCount = allDisplayItems.filter(p => p.status !== 'completed' && p.status !== 'stuck').length;
  const hasCompleted = allDisplayItems.some(p => p.status === 'completed');
  const hasStuck = allDisplayItems.some(p => p.status === 'stuck');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.9 }}
      className="fixed bottom-4 right-4 z-40"
    >
      <AnimatePresence mode="wait">
        {isExpanded ? (
          <motion.div
            key="expanded"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-[#121C2E] border border-[#1F2A44] rounded-xl shadow-[0_4px_24px_rgba(0,0,0,0.4)] w-80 overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#1F2A44] bg-[#0D1421]">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-[#4F7DF3] animate-spin" />
                <span className="text-sm font-medium text-[#E6ECF5]">
                  QA Processing ({processingCount})
                </span>
                {processingCount > 0 && (
                  <span className="w-2 h-2 bg-[#4F7DF3] rounded-full animate-pulse" />
                )}
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                aria-label="Collapse processing indicator"
                className="p-1 text-[#6B7280] hover:text-[#E6ECF5] hover:bg-[#1F2A44] rounded transition-colors"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {allDisplayItems.map((process) => (
                <div
                  key={process.id}
                  className={`p-4 border-b border-[#1F2A44] last:border-b-0 ${
                    process.status === 'completed' ? 'bg-[rgba(47,191,113,0.05)]' : ''
                  }${process.status === 'stuck' ? ' bg-[rgba(229,83,61,0.05)]' : ''}${process.isPending ? ' bg-[rgba(79,125,243,0.03)]' : ''}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#E6ECF5] truncate">
                        {process.projectName}
                      </p>
                      <p className={`text-xs mt-0.5 ${process.status === 'stuck' ? 'text-[#E5533D]' : 'text-[#6B7280]'}`}>
                        {getStatusText(process)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {process.status === 'completed' ? (
                        <CheckCircle className="w-4 h-4 text-[#2FBF71]" />
                      ) : process.status === 'stuck' ? (
                        <AlertTriangle className="w-4 h-4 text-[#E5533D]" />
                      ) : process.isPending ? (
                        <Sparkles className="w-4 h-4 text-[#4F7DF3] animate-pulse" />
                      ) : (
                        <button
                          onClick={() => onNavigateToProject?.(process.projectId)}
                          aria-label="View project"
                          className="p-1 text-[#6B7280] hover:text-[#4F7DF3] hover:bg-[#1F2A44] rounded transition-colors"
                          title="View project"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {process.isPending ? (
                    /* Progress bar for pending starts - real progress when available, indeterminate otherwise */
                    <div className="mb-2">
                      <div className="h-1.5 bg-[#1F2A44] rounded-full overflow-hidden">
                        {process.progress > 0 || process.status === 'forwarding_to_n8n' ? (
                          <motion.div
                            className="h-full bg-gradient-to-r from-[#4F7DF3] to-[#6366F1]"
                            initial={{ width: '0%' }}
                            animate={{ width: process.status === 'forwarding_to_n8n' ? '95%' : `${process.progress}%` }}
                            transition={{ duration: 0.3 }}
                          />
                        ) : (
                          <motion.div
                            className="h-full bg-gradient-to-r from-[#4F7DF3] to-[#6366F1] w-1/3"
                            animate={{ x: ['-100%', '300%'] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                          />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="mb-2">
                      <div className="h-1.5 bg-[#1F2A44] rounded-full overflow-hidden">
                        <motion.div
                          className={`h-full ${
                            process.status === 'completed'
                              ? 'bg-[#2FBF71]'
                              : process.status === 'stuck'
                              ? 'bg-[#E5533D]'
                              : 'bg-gradient-to-r from-[#4F7DF3] to-[#6366F1]'
                          }`}
                          initial={{ width: '0%' }}
                          animate={{ width: `${process.progress}%` }}
                          transition={{ duration: 0.3 }}
                        />
                      </div>
                    </div>
                  )}

                  {process.status === 'stuck' && process.projectId && (
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        onClick={() => handleRetryFailed(process.projectId)}
                        disabled={!!actionLoading}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-[#4F7DF3]/10 text-[#4F7DF3] border border-[#4F7DF3]/20 hover:bg-[#4F7DF3]/20 transition-colors disabled:opacity-50"
                      >
                        {actionLoading === `retry-${process.projectId}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3" />
                        )}
                        Retry Failed
                      </button>
                      <button
                        onClick={() => handleCancelStuck(process.projectId)}
                        disabled={!!actionLoading}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-[#E5533D]/10 text-[#E5533D] border border-[#E5533D]/20 hover:bg-[#E5533D]/20 transition-colors disabled:opacity-50"
                      >
                        {actionLoading === `cancel-${process.projectId}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <XCircle className="w-3 h-3" />
                        )}
                        Cancel
                      </button>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-xs text-[#6B7280]">
                    <span>
                      {process.isPending
                        ? (process.checkedPages > 0
                          ? `${process.checkedPages}/${process.totalPages} links checked`
                          : `${process.totalPages} pages queued`)
                        : `${process.completedPages}/${process.totalPages} pages`}
                    </span>
                    <span>
                      {process.status === 'completed'
                        ? 'Done'
                        : getElapsedTime(process.startedAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        ) : (
          <motion.button
            key="collapsed"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={() => setIsExpanded(true)}
            aria-label="Expand processing indicator"
            className={`flex items-center gap-3 px-4 py-2.5 rounded-full shadow-lg transition-all ${
              hasStuck
                ? 'bg-[rgba(229,83,61,0.15)] border border-[#E5533D]/30 hover:border-[#E5533D]/50'
                : hasCompleted && processingCount === 0
                ? 'bg-[rgba(47,191,113,0.15)] border border-[#2FBF71]/30 hover:border-[#2FBF71]/50'
                : 'bg-[#121C2E] border border-[#1F2A44] hover:border-[#4F7DF3]/50'
            }`}
          >
            {hasStuck ? (
              <AlertTriangle className="w-4 h-4 text-[#E5533D] flex-shrink-0" />
            ) : processingCount > 0 ? (
              <Loader2 className="w-4 h-4 text-[#4F7DF3] animate-spin flex-shrink-0" />
            ) : (
              <CheckCircle className="w-4 h-4 text-[#2FBF71] flex-shrink-0" />
            )}

            <span className={`text-sm font-medium whitespace-nowrap ${
              hasStuck
                ? 'text-[#E5533D]'
                : hasCompleted && processingCount === 0
                ? 'text-[#2FBF71]'
                : 'text-[#E6ECF5]'
            }`}>
              {getCollapsedText()}
            </span>

            <ChevronUp className="w-4 h-4 text-[#6B7280] flex-shrink-0" />
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default QAProcessingIndicator;
